/**
 * Hero Showcase Slider
 *
 * Auto-advancing property showcase with background sync.
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';

export default class HeroShowcase {
  constructor() {
    this.showcase = document.querySelector('[data-hero-showcase]');
    if (!this.showcase) return;

    this.slides = this.showcase.querySelectorAll('[data-showcase-slide]');
    this.heroImages = document.querySelectorAll('[data-hero-image]');
    this.progressBar = this.showcase.querySelector('[data-showcase-progress]');
    this.currentCounter = this.showcase.querySelector('[data-showcase-current]');
    this.prevBtn = this.showcase.querySelector('[data-showcase-prev]');
    this.nextBtn = this.showcase.querySelector('[data-showcase-next]');

    this.currentIndex = 0;
    this.totalSlides = this.slides.length;
    this.autoplayDuration = 6000; // 6 seconds per slide
    this.autoplayTimer = null;
    this.progressTween = null;
    this.isPaused = false;

    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;

    this.bindEvents();
    this.startAutoplay();

    // Entrance animation (CSS sets initial opacity: 0)
    gsap.fromTo(this.showcase,
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 1, delay: 0.8, ease: 'power3.out' }
    );
  }

  bindEvents() {
    this.prevBtn?.addEventListener('click', () => this.prev());
    this.nextBtn?.addEventListener('click', () => this.next());

    // Pause on hover
    this.showcase.addEventListener('mouseenter', () => this.pause());
    this.showcase.addEventListener('mouseleave', () => this.resume());

    // Keyboard navigation
    this.showcase.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
    });
  }

  goTo(index) {
    if (index === this.currentIndex) return;

    const prevIndex = this.currentIndex;
    this.currentIndex = index;

    // Update slides
    this.slides.forEach((slide, i) => {
      slide.classList.remove('is-active', 'is-prev');
      if (i === this.currentIndex) {
        slide.classList.add('is-active');
      } else if (i === prevIndex) {
        slide.classList.add('is-prev');
      }
    });

    // Update hero background images
    this.heroImages.forEach((img, i) => {
      if (i === this.currentIndex) {
        img.classList.add('is-active');
      } else {
        img.classList.remove('is-active');
      }
    });

    // Update counter
    if (this.currentCounter) {
      this.currentCounter.textContent = this.currentIndex + 1;
    }

    // Reset and restart progress bar
    this.resetProgress();
    if (!this.isPaused) {
      this.animateProgress();
    }
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.goTo(nextIndex);
  }

  prev() {
    const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.goTo(prevIndex);
  }

  startAutoplay() {
    this.animateProgress();
    this.scheduleNext();
  }

  scheduleNext() {
    this.clearAutoplay();
    this.autoplayTimer = setTimeout(() => {
      this.next();
      this.scheduleNext();
    }, this.autoplayDuration);
  }

  clearAutoplay() {
    if (this.autoplayTimer) {
      clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  animateProgress() {
    if (!this.progressBar) return;

    this.progressTween?.kill();
    this.progressTween = gsap.to(this.progressBar, {
      width: '100%',
      duration: this.autoplayDuration / 1000,
      ease: 'none',
    });
  }

  resetProgress() {
    this.progressTween?.kill();
    if (this.progressBar) {
      gsap.set(this.progressBar, { width: '0%' });
    }
  }

  pause() {
    this.isPaused = true;
    this.clearAutoplay();
    this.progressTween?.pause();
  }

  resume() {
    this.isPaused = false;
    this.progressTween?.resume();
    this.scheduleNext();
  }

  destroy() {
    this.clearAutoplay();
    this.progressTween?.kill();
  }
}
